/**
 * AppointmentsWebLeads — pestaña «Solicitudes web» (doc 0398 §4.4)
 *
 * Lo que llega del formulario de la web del cliente. NO son citas: no tienen
 * hora, ni profesional, ni han pasado por ninguna comprobación de
 * disponibilidad. Convertirlas en cita es una fase posterior — tocaría el motor
 * que ahora mismo está en refactor (0396).
 *
 * POR QUÉ SE REFRESCA SOLO
 *
 * La demostración consiste en que el cliente pulse Enviar en su propia web y vea
 * la solicitud aparecer. Si hay que darle a un botón para que aparezca, el
 * efecto se pierde: parece que el panel la fue a buscar, no que llegó. El
 * refresco automático se pausa cuando la pestaña del navegador no está visible,
 * para no consultar cada 10 segundos contra un portátil olvidado.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import BlockIcon from "@material-ui/icons/Block";
import CodeIcon from "@material-ui/icons/Code";
import moment from "moment";
import { useStyles } from "./styles";
import { softTone } from "../../theme/agendaTheme";
import { listWebLeads, updateWebLeadStatus } from "../../services/webLeads.api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const REFRESH_MS = 10000;

/**
 * Colores propios, deliberadamente NO añadidos a `AGENDA_STATUS_COLORS`: ese
 * mapa es la fuente única de los estados de una CITA, y mezclar ahí los de una
 * solicitud web haría creer que son el mismo ciclo de vida. No lo son.
 */
const WEB_LEAD_COLORS = {
  nueva: "#1976d2",
  vista: "#2e7d32",
  descartada: "#757575",
};

export default function AppointmentsWebLeads() {
  const classes = useStyles();
  const theme = useTheme();
  const agendaMode = (theme.agenda && theme.agenda.mode) || theme.palette.type;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payloadOf, setPayloadOf] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: "", label: i18n.t("appointmentsModule.webLeads.filterAll") },
      { value: "nueva", label: i18n.t("appointmentsModule.webLeads.statusNueva") },
      { value: "vista", label: i18n.t("appointmentsModule.webLeads.statusVista") },
      { value: "descartada", label: i18n.t("appointmentsModule.webLeads.statusDescartada") },
    ],
    [i18n.language],
  );

  // `silent` evita el parpadeo del refresco automático: solo la carga que pide
  // el usuario muestra el estado de espera.
  const fetchLeads = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await listWebLeads({ status: status || undefined, page, pageSize: rowsPerPage });
        if (!isMountedRef.current) return;
        setRows(data?.rows || []);
        setTotal(data?.total || 0);
        setNewCount(data?.newCount || 0);
      } catch (e) {
        if (!silent) toastError(e);
      } finally {
        if (isMountedRef.current && !silent) setLoading(false);
      }
    },
    [status, page, rowsPerPage],
  );

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") fetchLeads({ silent: true });
    };
    const timer = setInterval(tick, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchLeads]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateWebLeadStatus(id, newStatus);
      fetchLeads({ silent: true });
    } catch (e) {
      toastError(e);
    }
  };

  const pillStyle = (s) => softTone(WEB_LEAD_COLORS[s] || WEB_LEAD_COLORS.descartada, agendaMode);
  const statusLabel = (s) =>
    i18n.t(`appointmentsModule.webLeads.status${s.charAt(0).toUpperCase()}${s.slice(1)}`, { defaultValue: s });

  /**
   * «hace 4 segundos» y no una hora absoluta: los minutos los calcula el
   * servidor (`ageMinutes`), que es donde están los dos extremos de la resta. El
   * reloj del portátil del cliente no participa — y en una demostración en vivo,
   * ese reloj nunca coincide.
   */
  const receivedLabel = (lead) => {
    if (lead.ageMinutes < 1) return i18n.t("appointmentsModule.webLeads.justNow");
    if (lead.ageMinutes < 60)
      return i18n.t("appointmentsModule.webLeads.minutesAgo", { count: lead.ageMinutes });
    // `receivedAt` llega como ISO-8601 con `Z`: es un instante en UTC, porque la
    // conexión del backend trabaja en UTC aunque el servidor vaya en UTC−5
    // (doc 0397). Moment lo pasa a la hora de quien mira. Parsearlo con formato
    // explícito, como si fuera hora local, mostraría cinco horas de más.
    return moment(lead.receivedAt).format("L LT");
  };

  const formatPreferred = (value) =>
    value ? moment(value, "YYYY-MM-DD").format("L") : "—";

  const renderActions = (lead) => (
    <>
      <Tooltip title={i18n.t("appointmentsModule.webLeads.markSeen")}>
        <span>
          <IconButton
            size="small"
            disabled={lead.status === "vista"}
            onClick={() => handleStatusChange(lead.id, "vista")}
            aria-label={i18n.t("appointmentsModule.webLeads.markSeen")}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={i18n.t("appointmentsModule.webLeads.discard")}>
        <span>
          <IconButton
            size="small"
            disabled={lead.status === "descartada"}
            onClick={() => handleStatusChange(lead.id, "descartada")}
            aria-label={i18n.t("appointmentsModule.webLeads.discard")}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={i18n.t("appointmentsModule.webLeads.seePayload")}>
        <IconButton
          size="small"
          onClick={() => setPayloadOf(lead)}
          aria-label={i18n.t("appointmentsModule.webLeads.seePayload")}
        >
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
      <div>
        <Typography variant="body2" color="textSecondary">
          {i18n.t("appointmentsModule.webLeads.countLine", { count: total, pending: newCount })}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("appointmentsModule.webLeads.autoRefreshHint")}
        </Typography>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* `displayEmpty`: sin él, MUI trata la cadena vacía como «nada
            seleccionado» y deja el desplegable en blanco en vez de mostrar
            «Todas», que es justo el filtro activo al entrar. */}
        <TextField
          select
          size="small"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          style={{ minWidth: 150 }}
          SelectProps={{ displayEmpty: true }}
        >
          {statusOptions.map((o) => (
            <MenuItem key={o.value || "all"} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => fetchLeads()}
        >
          {i18n.t("appointmentsModule.webLeads.reload")}
        </Button>
      </div>
    </div>
  );

  const pagination = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage={i18n.t("appointmentsModule.historial.rowsPerPage")}
      labelDisplayedRows={({ from, to, count }) =>
        i18n.t("appointmentsModule.historial.displayedRows", { from, to, count })
      }
      style={{ marginTop: 8 }}
    />
  );

  const payloadDialog = (
    <Dialog open={!!payloadOf} onClose={() => setPayloadOf(null)} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("appointmentsModule.webLeads.payloadTitle")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("appointmentsModule.webLeads.payloadHint")}
        </Typography>
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.78rem", marginTop: 8 }}>
          {payloadOf ? JSON.stringify(payloadOf.rawPayload, null, 2) : ""}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPayloadOf(null)}>{i18n.t("appointmentsModule.webLeads.close")}</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Typography color="textSecondary">{i18n.t("appointmentsModule.webLeads.loading")}</Typography>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        {header}
        <div style={{ padding: 24, textAlign: "center" }}>
          <Typography color="textSecondary">{i18n.t("appointmentsModule.webLeads.empty")}</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {i18n.t("appointmentsModule.webLeads.emptyHint")}
          </Typography>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ padding: 16 }}>
        {header}
        {rows.map((lead) => (
          <Paper key={lead.id} className={classes.historialCard} elevation={0}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{lead.fullName}</Typography>
                <Typography variant="body2" color="textSecondary">{lead.phoneRaw}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {lead.serviceRequested || "—"} · {formatPreferred(lead.preferredDate)}
                </Typography>
                <Typography variant="caption" color="textSecondary">{receivedLabel(lead)}</Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={classes.statusPillSm} style={pillStyle(lead.status)}>
                  {statusLabel(lead.status)}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>{renderActions(lead)}</div>
          </Paper>
        ))}
        {pagination}
        {payloadDialog}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {header}
      <TableContainer component={Paper} className={classes.historialTableWrap} elevation={0}>
        <Table size="small" stickyHeader aria-label={i18n.t("appointmentsModule.webLeads.tableAria")}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colReceived")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colName")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colPhone")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colPreferred")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colService")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colSource")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colStatus")}</TableCell>
              <TableCell className={classes.historialTh} align="right">{i18n.t("appointmentsModule.webLeads.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((lead) => (
              <TableRow key={lead.id} hover className={classes.historialRow}>
                <TableCell>{receivedLabel(lead)}</TableCell>
                <TableCell>{lead.fullName}</TableCell>
                {/* El teléfono TAL COMO lo escribió el paciente: es lo que el
                    cliente reconoce como suyo. El normalizado va debajo, en
                    pequeño, porque es el que sirve para escribirle. */}
                <TableCell>
                  {lead.phoneRaw}
                  {lead.phoneE164 && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      +{lead.phoneE164}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatPreferred(lead.preferredDate)}</TableCell>
                <TableCell>{lead.serviceRequested || "—"}</TableCell>
                <TableCell>{lead.source}</TableCell>
                <TableCell>
                  <span className={classes.statusPill} style={pillStyle(lead.status)}>
                    {statusLabel(lead.status)}
                  </span>
                </TableCell>
                <TableCell align="right">{renderActions(lead)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
      {payloadDialog}
    </div>
  );
}
